import { useId } from "react";
import { useController, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { SettingCard } from "@/components/settings/setting-card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import type { CapacityFormData } from "../types";

interface DurationAwareToggleProps {
  disabled?: boolean;
}

/** Whether a booking counts toward the limits for its whole duration (`duration_aware`). */
export function DurationAwareToggle({ disabled = false }: DurationAwareToggleProps) {
  const { t } = useTranslation("settings");
  const id = useId();
  const descriptionId = `${id}-description`;
  const { control } = useFormContext<CapacityFormData>();
  const { field } = useController({ control, name: "durationAware" });
  const capacityMode = useWatch({ control, name: "capacityMode" });
  // Off counts a booking where it starts: its clock hour in basic mode, the
  // time-range rule it starts in in advanced mode.
  const offText =
    capacityMode === "advanced"
      ? t("capacity.durationAware.offAdvanced")
      : t("capacity.durationAware.off");
  const example = capacityMode === "advanced" ? "advanced" : "basic";

  return (
    <SettingCard
      title={t("capacity.durationAware.title")}
      description={t("capacity.durationAware.description")}
      helpDetails={
        <div className="space-y-3">
          <p className="font-medium text-foreground">{t("capacity.durationAware.example.title")}</p>
          {example === "basic" ? (
            <>
              <p>{t("capacity.durationAware.example.basic.setup")}</p>
              <ExampleList
                title={t("capacity.durationAware.example.onTitle")}
                items={[
                  t("capacity.durationAware.example.basic.on1"),
                  t("capacity.durationAware.example.basic.on2"),
                  t("capacity.durationAware.example.basic.on3"),
                ]}
              />
              <ExampleList
                title={t("capacity.durationAware.example.offTitle")}
                items={[
                  t("capacity.durationAware.example.basic.off1"),
                  t("capacity.durationAware.example.basic.off2"),
                  t("capacity.durationAware.example.basic.off3"),
                ]}
              />
            </>
          ) : (
            <>
              <p>{t("capacity.durationAware.example.advanced.setup")}</p>
              <ExampleList
                title={t("capacity.durationAware.example.onTitle")}
                items={[t("capacity.durationAware.example.advanced.on1")]}
              />
              <ExampleList
                title={t("capacity.durationAware.example.offTitle")}
                items={[t("capacity.durationAware.example.advanced.off1")]}
              />
            </>
          )}
          <ExampleList
            title={t("capacity.durationAware.example.when.title")}
            items={[
              t("capacity.durationAware.example.when.on"),
              t("capacity.durationAware.example.when.off"),
            ]}
          />
        </div>
      }
    >
      <div id="field-limits-duration-aware" className="flex items-center justify-between gap-4">
        <div className="min-w-0 space-y-1">
          <Label htmlFor={id} className="text-base font-medium">
            {t("capacity.durationAware.label")}
          </Label>
          <p id={descriptionId} className="text-sm text-muted-foreground">
            {field.value ? t("capacity.durationAware.on") : offText}
          </p>
        </div>
        <Switch
          id={id}
          aria-describedby={descriptionId}
          checked={field.value}
          onCheckedChange={field.onChange}
          disabled={disabled}
        />
      </div>
    </SettingCard>
  );
}

function ExampleList({ title, items }: { title: string; items: string[] }) {
  return (
    <div className="space-y-1">
      <p className="font-medium text-foreground">{title}</p>
      <ul className="list-disc space-y-1 pl-4">
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </div>
  );
}
